import React from 'react';
import PropTypes from 'prop-types';
import { CheckCircle, RefreshCw, Link as LinkIcon, Unlink } from 'lucide-react';
import ahoy from 'ahoy.js';

function QboBadge({ connected }) {
  if (connected) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-wide px-2.5 py-1 rounded-full"
        style={{
          backgroundColor: 'hsl(var(--succeedo-orange) / 0.1)',
          color: 'hsl(var(--succeedo-orange))',
          border: '1px solid hsl(var(--succeedo-orange) / 0.2)',
        }}
      >
        <CheckCircle className="w-3.5 h-3.5" />
        Connected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-wide px-2.5 py-1 rounded-full bg-background border border-border text-muted-foreground">
      Not connected
    </span>
  );
}

QboBadge.propTypes = { connected: PropTypes.bool.isRequired };

// QboConnectionCard — connection status header + sync/disconnect/connect
// actions. Pure UI; the parent owns the sync handler and CSRF concerns.
export default function QboConnectionCard({
  connected,
  formattedSyncDate,
  syncing,
  syncUrl,
  connectUrl,
  disconnectUrl,
  onSyncNow,
}) {
  return (
    <div className="bg-white rounded-[16px] p-6 shadow-sm border border-border mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <img
            src="https://findbob-production.s3.us-west-2.amazonaws.com/images/qbo_logomark.png"
            alt="QuickBooks"
            className="w-10 h-10 rounded-[10px] object-contain flex-shrink-0"
          />
          <div>
            <p className="text-[15px] font-semibold text-foreground">QuickBooks Online</p>
            <p className="text-[12px] text-muted-foreground">Syncs revenue &amp; expense data</p>
          </div>
        </div>
        <QboBadge connected={connected} />
      </div>

      {connected ? (
        <div className="space-y-4">
          {formattedSyncDate && (
            <p className="text-[13px] text-muted-foreground flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" />
              Last synced: {formattedSyncDate}
            </p>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onSyncNow}
              disabled={syncing || !syncUrl}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-medium text-background transition-colors disabled:opacity-50"
              style={{ backgroundColor: 'hsl(var(--succeedo-orange))' }}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing…' : 'Sync now'}
            </button>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href={disconnectUrl || '#'}
              data-method="delete"
              rel="nofollow"
              onClick={(e) => {
                if (!window.confirm('Disconnect QuickBooks? Your synced data will be cleared.')) e.preventDefault();
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-[13px] text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
            >
              <Unlink className="w-3.5 h-3.5" />
              Disconnect
            </a>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-[14px] text-muted-foreground mb-4">
            Connect your QuickBooks Online account to automatically pull your P&amp;L data into the Valuation Studio.
            Your annual revenue, operating expenses, and 3-year historical revenue will be pre-filled.
          </p>
          <a
            href={connectUrl || '#'}
            onClick={() => ahoy.track('succeedo_qbo_connect_clicked')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium text-[14px] text-background transition-colors"
            style={{ backgroundColor: 'hsl(var(--succeedo-orange))' }}
          >
            <LinkIcon className="w-4 h-4" />
            Connect QuickBooks
          </a>
        </div>
      )}
    </div>
  );
}

QboConnectionCard.propTypes = {
  connected: PropTypes.bool.isRequired,
  formattedSyncDate: PropTypes.string,
  syncing: PropTypes.bool,
  syncUrl: PropTypes.string,
  connectUrl: PropTypes.string,
  disconnectUrl: PropTypes.string,
  onSyncNow: PropTypes.func.isRequired,
};

QboConnectionCard.defaultProps = {
  formattedSyncDate: null,
  syncing: false,
  syncUrl: null,
  connectUrl: null,
  disconnectUrl: null,
};
